"use client";

import { createContext, useContext, useState } from 'react';

// Base subcategories — used to seed the editable list on first load
const BASE_SUBCATEGORIES: Record<string, string[]> = {
  CAMERA: ['AKS', 'Adapters', 'BNC Cables', 'Batteries', 'Cam Misc.', 'Camera Accessories', 'Camera Body', 'Data Cables', 'Fluid Heads', 'Lens', 'Media', 'Meters', 'Monitor', 'Photography', 'Stabilizers', 'Tripods', 'Video Cables', 'Wireless Focus Systems', 'Wireless Video Transmission'],
  SOUND: ['Headphones', 'Lav Mic', 'Recorders', 'Shotgun Mic', 'Support', 'XLR Cable'],
  GRIP: ['Adapters', 'Bounce Boards', 'C-Stands', 'Dolly Accessories', 'Flags', 'Frames', 'Grip Clamps', 'Ground Tools', 'Hardware', 'Rags', 'Reflector Boards', 'Rigging Hardware', 'Sliders', 'Spuds & Pins', 'Weight (Bags)', 'Weight (Plates)'],
  ELECTRIC: ['Dimmer', 'Distro', 'HMI', 'Modifiers', 'Snakebites', 'Splitters & Adapters', 'Stingers', 'TUNGSTEN'],
  PRODUCTION: ['ART Props', 'Walkie', 'Walkie Acc.'],
};

interface AdminConfig {
  locations: string[];
  /** Room → spots within that room (Intake / Inspector). */
  sublocations: Record<string, string[]>;
  manufacturers: string[];
  acquisitionSources: string[];
  purchaseSources: string[];
  /** Values staff removed from a curated list — don't re-fold from item fields. */
  suppressedManufacturers: string[];
  suppressedAcquisitionSources: string[];
  suppressedPurchaseSources: string[];
  subcategories: Record<string, string[]>;
}

type ListKey = 'locations' | 'manufacturers' | 'acquisitionSources' | 'purchaseSources';

interface AdminConfigContextValue {
  config: AdminConfig;
  addToList: (key: ListKey, value: string) => boolean;
  removeFromList: (key: ListKey, value: string) => void;
  renameInList: (key: ListKey, oldValue: string, newValue: string) => boolean;
  /** Add missing values without duplicates (case-insensitive). Used to fold live item fields into the curated list. */
  mergeIntoList: (key: ListKey, values: string[]) => void;
  addSublocation: (location: string, value: string) => boolean;
  removeSublocation: (location: string, value: string) => void;
  // Legacy subcategory helpers (Admin UI removed; kept for any leftover callers)
  addSubcategory: (category: string, value: string) => boolean;
  removeSubcategory: (category: string, value: string) => void;
  renameSubcategory: (category: string, oldValue: string, newValue: string) => boolean;
}

const STORAGE_KEY = 'vfc-admin-config';

const DEFAULTS: AdminConfig = {
  locations: ['SAE - 100', 'SAE - 108', 'SAE - 115', 'SAE - ATTIC'],
  sublocations: {},
  manufacturers: [],
  acquisitionSources: ['Purchase', 'Donation', 'Grant'],
  purchaseSources: [],
  suppressedManufacturers: [],
  suppressedAcquisitionSources: [],
  suppressedPurchaseSources: [],
  subcategories: BASE_SUBCATEGORIES,
};

function loadConfig(): AdminConfig {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      locations: stored.locations ?? [...DEFAULTS.locations],
      sublocations: stored.sublocations ?? {},
      manufacturers: stored.manufacturers ?? [],
      acquisitionSources: stored.acquisitionSources?.length
        ? stored.acquisitionSources
        : [...DEFAULTS.acquisitionSources],
      purchaseSources: stored.purchaseSources ?? [],
      suppressedManufacturers: stored.suppressedManufacturers ?? [],
      suppressedAcquisitionSources: stored.suppressedAcquisitionSources ?? [],
      suppressedPurchaseSources: stored.suppressedPurchaseSources ?? [],
      // If no subcategories stored yet, seed from BASE_SUBCATEGORIES
      subcategories: stored.subcategories ?? { ...BASE_SUBCATEGORIES },
    };
  } catch {
    return { ...DEFAULTS };
  }
}

const AdminConfigContext = createContext<AdminConfigContextValue | null>(null);

export const useAdminConfig = () => {
  const ctx = useContext(AdminConfigContext);
  if (!ctx) throw new Error('useAdminConfig must be within AdminConfigProvider');
  return ctx;
};

export function AdminConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AdminConfig>(loadConfig);

  function persist(next: AdminConfig) {
    setConfig(next);
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  function addToList(key: ListKey, value: string): boolean {
    const trimmed = value.trim();
    const list = config[key];
    if (!trimmed || list.some(v => v.toLowerCase() === trimmed.toLowerCase())) return false;
    const next: AdminConfig = { ...config, [key]: [...list, trimmed].sort((a, b) => a.localeCompare(b)) };
    if (key === 'manufacturers') {
      next.suppressedManufacturers = config.suppressedManufacturers.filter(
        (v) => v.toLowerCase() !== trimmed.toLowerCase()
      );
    }
    if (key === 'acquisitionSources') {
      next.suppressedAcquisitionSources = config.suppressedAcquisitionSources.filter(
        (v) => v.toLowerCase() !== trimmed.toLowerCase()
      );
    }
    if (key === 'purchaseSources') {
      next.suppressedPurchaseSources = config.suppressedPurchaseSources.filter(
        (v) => v.toLowerCase() !== trimmed.toLowerCase()
      );
    }
    persist(next);
    return true;
  }

  function removeFromList(key: ListKey, value: string) {
    const list = config[key];
    const next: AdminConfig = { ...config, [key]: list.filter(v => v !== value) };
    if (key === 'locations') {
      const { [value]: _removed, ...rest } = next.sublocations;
      next.sublocations = rest;
    }
    if (key === 'manufacturers') {
      const lower = value.toLowerCase();
      if (!next.suppressedManufacturers.some((v) => v.toLowerCase() === lower)) {
        next.suppressedManufacturers = [...next.suppressedManufacturers, value];
      }
    }
    if (key === 'acquisitionSources') {
      const lower = value.toLowerCase();
      if (!next.suppressedAcquisitionSources.some((v) => v.toLowerCase() === lower)) {
        next.suppressedAcquisitionSources = [...next.suppressedAcquisitionSources, value];
      }
    }
    if (key === 'purchaseSources') {
      const lower = value.toLowerCase();
      if (!next.suppressedPurchaseSources.some((v) => v.toLowerCase() === lower)) {
        next.suppressedPurchaseSources = [...next.suppressedPurchaseSources, value];
      }
    }
    persist(next);
  }

  function renameInList(key: ListKey, oldValue: string, newValue: string): boolean {
    const trimmed = newValue.trim();
    const list = config[key];
    if (!trimmed || trimmed === oldValue || list.some(v => v.toLowerCase() === trimmed.toLowerCase())) return false;
    const next: AdminConfig = {
      ...config,
      [key]: list.map(v => (v === oldValue ? trimmed : v)),
    };
    if (key === 'locations' && config.sublocations[oldValue]) {
      const { [oldValue]: spots, ...rest } = next.sublocations;
      next.sublocations = { ...rest, [trimmed]: spots ?? [] };
    }
    persist(next);
    return true;
  }

  function mergeIntoList(key: ListKey, values: string[]) {
    const list = config[key];
    const suppressed =
      key === 'manufacturers' ? config.suppressedManufacturers
      : key === 'acquisitionSources' ? config.suppressedAcquisitionSources
      : key === 'purchaseSources' ? config.suppressedPurchaseSources
      : [];
    const suppressedSet = new Set(suppressed.map((v) => v.toLowerCase()));
    const seen = new Set(list.map(v => v.toLowerCase()));
    const toAdd: string[] = [];
    for (const raw of values) {
      const trimmed = raw.trim();
      if (!trimmed) continue;
      const lower = trimmed.toLowerCase();
      if (seen.has(lower) || suppressedSet.has(lower)) continue;
      seen.add(lower);
      toAdd.push(trimmed);
    }
    if (toAdd.length === 0) return;
    persist({
      ...config,
      [key]: [...list, ...toAdd].sort((a, b) => a.localeCompare(b)),
    });
  }

  function addSublocation(location: string, value: string): boolean {
    const trimmed = value.trim();
    const current = config.sublocations[location] ?? [];
    if (!trimmed || current.includes(trimmed)) return false;
    persist({
      ...config,
      sublocations: { ...config.sublocations, [location]: [...current, trimmed].sort() },
    });
    return true;
  }

  function removeSublocation(location: string, value: string) {
    const current = config.sublocations[location] ?? [];
    persist({
      ...config,
      sublocations: {
        ...config.sublocations,
        [location]: current.filter(v => v !== value),
      },
    });
  }

  // ── Subcategory helpers (legacy; Admin UI removed) ──

  function addSubcategory(category: string, value: string): boolean {
    const trimmed = value.trim();
    const current = config.subcategories[category] ?? [];
    if (!trimmed || current.includes(trimmed)) return false;
    const updated = [...current, trimmed].sort();
    persist({ ...config, subcategories: { ...config.subcategories, [category]: updated } });
    return true;
  }

  function removeSubcategory(category: string, value: string) {
    const current = config.subcategories[category] ?? [];
    persist({ ...config, subcategories: { ...config.subcategories, [category]: current.filter(v => v !== value) } });
  }

  function renameSubcategory(category: string, oldValue: string, newValue: string): boolean {
    const trimmed = newValue.trim();
    const current = config.subcategories[category] ?? [];
    if (!trimmed || trimmed === oldValue || current.includes(trimmed)) return false;
    const updated = current.map(v => v === oldValue ? trimmed : v).sort();
    persist({ ...config, subcategories: { ...config.subcategories, [category]: updated } });
    return true;
  }

  return (
    <AdminConfigContext.Provider value={{
      config, addToList, removeFromList, renameInList, mergeIntoList,
      addSublocation, removeSublocation,
      addSubcategory, removeSubcategory, renameSubcategory,
    }}>
      {children}
    </AdminConfigContext.Provider>
  );
}
